<template>
  <div class="search-wrapper">
    <adn-input-text
      v-model="searchTxt"
      class="search-filters-search__input"
      icon-left="function-search"
      :placeholder="i18n._('Rechercher...')"
      :icon-right="isInputSearchFilterEmpty ? '' : 'navigation-close'"
      :has-right-click="true"
      data-test-input-search-filter
      @on-icon-right-click="clearInputText"
    ></adn-input-text>
    <ul class="search-filter__list-items">
      <li v-for="item in displayedList" :key="item.label" class="search-filter__list-item">
        <div class="search-filter__list-item-label-wrapper">
          <template v-if="props.selectType === 'radio'">
            <adn-radio-button
              :group-name="'crops'"
              :value="item.value"
              :initial-value="initialValue()"
              @update:modelValue="updateSelection(item.value)"
            >
              <span class="search-filter__list-item-label">{{ item.label }}</span>
            </adn-radio-button>
          </template>
          <template v-else>
            <adn-checkbox :checked="isSelected(item.value)" @change="updateSelection(item.value)">
              <span class="search-filter__list-item-label">{{ item.label }}</span>
            </adn-checkbox>
          </template>
        </div>
        <span class="search-filter__list-item-count">{{ item.count }}</span>
      </li>
    </ul>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import AdnInputText from '@b2ag/design-system/dist/components/AdnInputText/AdnInputText.vue'
import AdnRadioButton from '@b2ag/design-system/dist/components/AdnRadioButton/AdnRadioButton.vue'
import AdnCheckbox from '@b2ag/design-system/dist/components/AdnCheckbox/AdnCheckbox.vue'
import { i18n } from '@b2ag/locale'

const props = defineProps({
  selectType: {
    type: String as () => 'radio' | 'checkbox',
    required: true,
    default: 'checkbox',
  },
  items: {
    type: Array as () => Array<{ label: string; value: string; count: number }>,
    required: true,
  },
  values: {
    type: Array as () => Array<string>,
    required: true,
  },
  sortBy: {
    type: String as () => 'label' | 'count',
    default: 'count',
  },
  sortDirection: {
    type: String as () => 'asc' | 'desc',
    default: 'desc',
  },
})

const emit = defineEmits(['update', 'search'])

const searchTxt = ref('')
const displayedList = ref<Array<{ label: string; value: string; count: number }>>([])
const isInputSearchFilterEmpty = computed(() => searchTxt.value === '')

const updateDisplayedItems = () => {
  if (!props.items || props.items.length === 0) return

  displayedList.value = props.items
    .filter((item) =>
      searchTxt.value !== '' ? item.label.toLowerCase().includes(searchTxt.value.toLowerCase()) : true,
    )
    .sort((a, b) => {
      if (props.sortBy === 'label') {
        return props.sortDirection === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label)
      }
      return props.sortDirection === 'asc' ? a.count - b.count : b.count - a.count
    })
}

const clearInputText = () => {
  searchTxt.value = ''
  emit('search', '')
}

const isSelected = (value: string) => {
  return Array.isArray(props.values) && props.values.includes(value)
}

const initialValue = () => {
  return props.values && props.values.length > 0 ? props.values[0] : null
}

const updateSelection = (value: string) => {
  let newValues: string[]
  const currentValues = Array.isArray(props.values) ? props.values : []

  if (props.selectType === 'radio') {
    newValues = [value]
  } else {
    if (currentValues.includes(value)) {
      newValues = currentValues.filter((v) => v !== value)
    } else {
      newValues = [...currentValues, value]
    }
  }

  emit('update', newValues)
}

watch(searchTxt, () => {
  updateDisplayedItems()
  emit('search', searchTxt.value)
})

watch(
  () => props.items,
  () => {
    updateDisplayedItems()
  },
  { immediate: true },
)

onMounted(() => {
  updateDisplayedItems()
})
</script>
<style lang="scss">
.search-wrapper {
  width: 23.2rem;
}

.search-filter__list-items {
  position: relative;
  max-height: 21.3rem;
  overflow-y: auto;
  margin-top: var(--spacing-3);
  padding: 0;
  display: flex;
  flex-direction: column;

  .search-filter__list-item {
    position: relative;
    line-height: 2.4rem;
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-1);
    padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) 0;

    &:first-child {
      padding-top: 0;
    }

    &-label {
      display: inline-block;
      color: var(--color-black);
      font-weight: 400;
      flex-grow: 1;
      &-radio {
        margin-top: 0;
      }
    }

    &-count {
      color: var(--color-text-light);
      font-size: var(--font-size-m);
      font-weight: 400;
    }

    &-label-wrapper {
      display: inline-block;
    }
  }
}
</style>
